/**
 * Security route — UFW, fail2ban, SSH, ports, SSL.
 */

import { useQuery } from "@tanstack/react-query";
import { Shield, Lock, Network, Key, AlertTriangle } from "lucide-react";
import { CorpoCard } from "../components/ui/CorpoCard";
import { CRTHeader } from "../components/ui/CRTHeader";
import { StatusDot } from "../components/ui/StatusDot";
import { HexBadge } from "../components/ui/HexBadge";
import { GlyphButton } from "../components/ui/GlyphButton";

interface UfwRule {
  action: string;
  from: string;
  to: string;
  port: string;
}

interface Fail2banInfo {
  jails: string[];
  bannedCount: number;
}

interface SshAuthInfo {
  recentFailures: number;
  recentSuccesses: number;
  lastFailure: string | null;
  lastSuccess: string | null;
}

interface OpenPort {
  protocol: string;
  port: number;
  address: string;
  process: string | null;
}

interface SslCert {
  domain: string;
  expiresAt: string;
  issuer: string;
}

interface SecurityStatus {
  ufwRules: UfwRule[];
  fail2ban: Fail2banInfo;
  sshAuth: SshAuthInfo;
  openPorts: OpenPort[];
  sslCerts: SslCert[];
}

function daysUntilExpiry(expiresAt: string): number {
  try {
    const expDate = new Date(expiresAt);
    const now = new Date();
    const diffMs = expDate.getTime() - now.getTime();
    return Math.floor(diffMs / (1000 * 60 * 60 * 24));
  } catch {
    return 999; // Unknown, assume OK
  }
}

export function SecurityRoute() {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["security-status"],
    queryFn: async () => {
      const res = await fetch("/api/security/status");
      return res.json() as Promise<SecurityStatus>;
    },
  });

  if (isLoading) {
    return (
      <div className="p-4 md:p-6">
        <CRTHeader text="Seguridad" level={2} />
        <div className="mt-6 text-[var(--bw-muted)] animate-pulse">
          ESCANEANDO SISTEMA...
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="p-4 md:p-6 space-y-6">
        <CRTHeader text="Seguridad" level={2} />
        <CorpoCard className="border-[var(--bw-danger)]">
          <p className="text-[var(--bw-danger)]">
            ⚠ ERROR: {error instanceof Error ? error.message : "Sin datos"}
          </p>
        </CorpoCard>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 space-y-6">
      <div className="flex items-center justify-between">
        <CRTHeader text="Seguridad" level={2} />
        <GlyphButton variant="secondary" size="sm" onClick={() => refetch()}>
          Escanear
        </GlyphButton>
      </div>

      {/* UFW Rules */}
      <CorpoCard title="UFW FIREWALL">
        {data.ufwRules.length === 0 ? (
          <div className="flex items-center gap-2 text-[var(--bw-warn)]">
            <AlertTriangle size={16} />
            <span>Sin reglas UFW detectadas</span>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-[var(--bw-muted)] uppercase border-b border-[var(--bw-border)]">
                  <th className="py-2 pr-4">Port</th>
                  <th className="py-2 pr-4">Action</th>
                  <th className="py-2 pr-4">From</th>
                  <th className="py-2">To</th>
                </tr>
              </thead>
              <tbody>
                {data.ufwRules.map((rule, idx) => (
                  <tr
                    key={idx}
                    className="border-b border-[var(--bw-border)] last:border-b-0"
                  >
                    <td className="py-1.5 pr-4 font-mono text-[var(--bw-accent)]">
                      {rule.port}
                    </td>
                    <td className="py-1.5 pr-4">
                      <HexBadge
                        label={rule.action}
                        variant={rule.action === "ALLOW" ? "success" : "danger"}
                      />
                    </td>
                    <td className="py-1.5 pr-4 text-[var(--bw-muted)]">
                      {rule.from}
                    </td>
                    <td className="py-1.5 text-[var(--bw-fg)]">{rule.to}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CorpoCard>

      <div className="grid gap-4 lg:grid-cols-2">
        {/* Fail2ban */}
        <CorpoCard title="FAIL2BAN">
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-[var(--bw-muted)]">Jails activos:</span>
              <span className="text-sm font-bold text-[var(--bw-fg)]">
                {data.fail2ban.jails.length}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-[var(--bw-muted)]">IPs baneadas:</span>
              <StatusDot
                status={data.fail2ban.bannedCount > 0 ? "warning" : "online"}
                label={String(data.fail2ban.bannedCount)}
              />
            </div>
            {data.fail2ban.jails.length > 0 && (
              <div className="flex flex-wrap gap-2 pt-2 border-t border-[var(--bw-border)]">
                {data.fail2ban.jails.map((jail) => (
                  <HexBadge key={jail} label={jail} variant="accent" />
                ))}
              </div>
            )}
          </div>
        </CorpoCard>

        {/* SSH Auth */}
        <CorpoCard title="AUTENTICACIÓN SSH">
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Lock size={14} className="text-[var(--bw-success)]" />
                <span className="text-sm text-[var(--bw-muted)]">Éxitos recientes:</span>
              </div>
              <HexBadge
                label={String(data.sshAuth.recentSuccesses)}
                variant="success"
              />
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <AlertTriangle size={14} className="text-[var(--bw-danger)]" />
                <span className="text-sm text-[var(--bw-muted)]">Fallos recientes:</span>
              </div>
              <HexBadge
                label={String(data.sshAuth.recentFailures)}
                variant={data.sshAuth.recentFailures > 10 ? "danger" : "warning"}
              />
            </div>
            {data.sshAuth.lastFailure && (
              <p className="text-xs text-[var(--bw-muted)] pt-2 border-t border-[var(--bw-border)]">
                Último fallo: {data.sshAuth.lastFailure}
              </p>
            )}
          </div>
        </CorpoCard>
      </div>

      {/* Open Ports */}
      <CorpoCard title="PUERTOS ABIERTOS">
        {data.openPorts.length === 0 ? (
          <p className="text-[var(--bw-muted)]">Sin puertos TCP detectados</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-[var(--bw-muted)] uppercase border-b border-[var(--bw-border)]">
                  <th className="py-2 pr-4">Proto</th>
                  <th className="py-2 pr-4">Port</th>
                  <th className="py-2 pr-4">Address</th>
                  <th className="py-2">Process</th>
                </tr>
              </thead>
              <tbody>
                {data.openPorts.map((port, idx) => (
                  <tr
                    key={idx}
                    className="border-b border-[var(--bw-border)] last:border-b-0"
                  >
                    <td className="py-1.5 pr-4 font-mono text-[var(--bw-muted)]">
                      {port.protocol}
                    </td>
                    <td className="py-1.5 pr-4 font-mono text-[var(--bw-accent)]">
                      {port.port}
                    </td>
                    <td className="py-1.5 pr-4 text-[var(--bw-fg)]">
                      {port.address}
                    </td>
                    <td className="py-1.5 text-[var(--bw-muted)]">
                      {port.process ?? "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CorpoCard>

      {/* SSL Certs */}
      <CorpoCard title="CERTIFICADOS SSL">
        {data.sslCerts.length === 0 ? (
          <div className="flex items-center gap-2 text-[var(--bw-muted)]">
            <Key size={16} />
            <span>Sin certificados detectados</span>
          </div>
        ) : (
          <div className="grid gap-4 md:grid-cols-2">
            {data.sslCerts.map((cert) => {
              const daysLeft = daysUntilExpiry(cert.expiresAt);
              const isExpiringSoon = daysLeft < 30;
              const isExpired = daysLeft < 0;

              return (
                <div
                  key={cert.domain}
                  className={`
                    p-3 border rounded-sm
                    ${isExpired
                      ? "border-[var(--bw-danger)] bg-[var(--bw-danger)]/10"
                      : isExpiringSoon
                        ? "border-[var(--bw-warn)] bg-[var(--bw-warn)]/10"
                        : "border-[var(--bw-border)]"
                    }
                  `}
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-mono text-sm text-[var(--bw-fg)]">
                      {cert.domain}
                    </span>
                    <StatusDot
                      status={isExpired ? "error" : isExpiringSoon ? "warning" : "online"}
                      size="sm"
                    />
                  </div>
                  <p className="text-xs text-[var(--bw-muted)]">
                    Expira: <span className={isExpired || isExpiringSoon ? "text-[var(--bw-danger)]" : ""}>
                      {cert.expiresAt}
                    </span>
                  </p>
                  <p className="text-xs text-[var(--bw-muted)]">
                    Emisor: {cert.issuer}
                  </p>
                </div>
              );
            })}
          </div>
        )}
      </CorpoCard>
    </div>
  );
}
